import React, { useEffect, useState, useRef, useCallback, memo } from "react";
import { createPortal } from "react-dom";
import { FaEdit, FaTrash, FaPlus, FaTimes, FaBlog, FaSearch, FaThLarge, FaList, FaChevronLeft, FaChevronRight, FaCalendar, FaTag, FaImage, FaEye } from 'react-icons/fa';
import ReactQuill from 'react-quill';
import 'react-quill/dist/quill.snow.css';
import PageLayout from "../../components/ui/PageLayout";
import PageHeader from "../../components/ui/PageHeader";

const slugify = (text = "") =>
  text.toString().trim().toLowerCase()
    .replace(/[^a-z0-9\s-]/g, "")
    .replace(/\s+/g, "-")
    .replace(/-+/g, "-")
    .replace(/^-+|-+$/g, "");

// ─── Skeleton Components ────────────────────────────────────────────────
const TableRowSkeleton = () => (
  <tr className="animate-pulse">
    <td className="px-5 py-4"><div className="h-4 w-8 bg-gray-200 dark:bg-dark-600 rounded" /></td>
    <td className="px-5 py-4">
      <div className="flex items-center gap-3">
        <div className="w-12 h-12 bg-gray-200 dark:bg-dark-600 rounded-xl flex-shrink-0" />
        <div className="space-y-1.5 flex-1">
          <div className="h-4 w-3/4 bg-gray-200 dark:bg-dark-600 rounded" />
          <div className="h-3 w-1/3 bg-gray-100 dark:bg-dark-700 rounded" />
        </div>
      </div>
    </td>
    <td className="px-5 py-4"><div className="h-5 w-16 bg-gray-200 dark:bg-dark-600 rounded-full" /></td>
    <td className="px-5 py-4"><div className="h-5 w-16 bg-gray-200 dark:bg-dark-600 rounded-full" /></td>
    <td className="px-5 py-4"><div className="h-3 w-24 bg-gray-200 dark:bg-dark-600 rounded" /></td>
    <td className="px-5 py-4"><div className="flex gap-2 justify-center"><div className="h-8 w-8 bg-gray-200 dark:bg-dark-600 rounded-lg" /><div className="h-8 w-8 bg-gray-200 dark:bg-dark-600 rounded-lg" /><div className="h-8 w-8 bg-gray-200 dark:bg-dark-600 rounded-lg" /></div></td>
  </tr>
);

const CardSkeleton = () => (
  <div className="bg-white dark:bg-dark-800 rounded-2xl shadow-card overflow-hidden animate-pulse">
    <div className="h-44 bg-gray-200 dark:bg-dark-600" />
    <div className="p-5 space-y-3">
      <div className="h-5 w-3/4 bg-gray-200 dark:bg-dark-600 rounded" />
      <div className="h-3 w-full bg-gray-100 dark:bg-dark-700 rounded" />
      <div className="h-3 w-2/3 bg-gray-100 dark:bg-dark-700 rounded" />
      <div className="flex items-center justify-between pt-2">
        <div className="h-5 w-16 bg-gray-200 dark:bg-dark-600 rounded-full" />
        <div className="h-3 w-20 bg-gray-200 dark:bg-dark-600 rounded" />
      </div>
    </div>
  </div>
);

// ─── Memoized Blog Row ──────────────────────────────────────────────────
const BlogTableRow = memo(({ blog, index, onEdit, onDelete, onPreview, apiBaseUrl }) => {
  const formattedDate = new Date(blog.createdAt).toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' });

  return (
    <tr className="group hover:bg-primary-50/50 dark:hover:bg-dark-700/50 transition-all duration-200">
      <td className="px-5 py-4 text-sm font-medium text-gray-500 dark:text-gray-400">{index}</td>
      <td className="px-5 py-4">
        <div className="flex items-center gap-3">
          {blog.image ? (
            <img src={`${apiBaseUrl}/${blog.image}`} alt={blog.title} className="w-12 h-12 object-cover rounded-xl flex-shrink-0 shadow-sm" loading="lazy" />
          ) : (
            <div className="w-12 h-12 bg-gradient-to-br from-gray-100 to-gray-200 dark:from-dark-600 dark:to-dark-700 rounded-xl flex items-center justify-center flex-shrink-0">
              <FaImage className="w-4 h-4 text-gray-400" />
            </div>
          )}
          <div className="min-w-0">
            <p className="text-sm font-semibold text-gray-900 dark:text-white truncate max-w-xs">{blog.title}</p>
            <p className="text-xs text-gray-400 dark:text-gray-500 font-mono truncate max-w-xs">/{blog.slug}</p>
          </div>
        </div>
      </td>
      <td className="px-5 py-4">
        <span className="inline-flex items-center gap-1 px-2.5 py-1 text-xs font-semibold bg-primary-50 text-primary-700 dark:bg-primary-900/30 dark:text-primary-400 rounded-full">
          <FaTag className="w-2.5 h-2.5" />
          {blog.category || "General"}
        </span>
      </td>
      <td className="px-5 py-4">
        {blog.status === 'Draft' && (
          <span className="inline-flex items-center px-2.5 py-1 text-xs font-semibold bg-yellow-50 text-yellow-700 dark:bg-yellow-900/30 dark:text-yellow-400 rounded-full">
            Draft
          </span>
        )}
        {blog.status === 'Scheduled' && (
          <span className="inline-flex items-center px-2.5 py-1 text-xs font-semibold bg-purple-50 text-purple-700 dark:bg-purple-900/30 dark:text-purple-400 rounded-full" title={blog.scheduledAt ? `Scheduled for: ${new Date(blog.scheduledAt).toLocaleString()}` : 'Scheduled'}>
            Scheduled
          </span>
        )}
        {(blog.status === 'Published' || !blog.status) && (
          <span className="inline-flex items-center px-2.5 py-1 text-xs font-semibold bg-green-50 text-green-700 dark:bg-green-900/30 dark:text-green-400 rounded-full">
            Published
          </span>
        )}
      </td>
      <td className="px-5 py-4">
        <span className="text-xs text-gray-500 dark:text-gray-400 flex items-center gap-1.5">
          <FaCalendar className="w-3 h-3" />
          {formattedDate}
        </span>
      </td>
      <td className="px-5 py-4">
        <div className="flex justify-center gap-1 opacity-60 group-hover:opacity-100 transition-opacity">
          <button onClick={() => onPreview(blog)} className="p-2 text-gray-500 hover:text-primary-600 hover:bg-primary-50 dark:hover:bg-primary-900/20 rounded-lg transition-all" title="Preview">
            <FaEye className="w-3.5 h-3.5" />
          </button>
          <button onClick={() => onEdit(blog)} className="p-2 text-gray-500 hover:text-primary-600 hover:bg-primary-50 dark:hover:bg-primary-900/20 rounded-lg transition-all" title="Edit">
            <FaEdit className="w-3.5 h-3.5" />
          </button>
          <button onClick={() => onDelete(blog._id)} className="p-2 text-gray-500 hover:text-red-600 hover:bg-red-50 dark:hover:bg-red-900/20 rounded-lg transition-all" title="Delete">
            <FaTrash className="w-3.5 h-3.5" />
          </button>
        </div>
      </td>
    </tr>
  );
});
BlogTableRow.displayName = 'BlogTableRow';

// ─── Memoized Blog Card ─────────────────────────────────────────────────
const BlogCard = memo(({ blog, onEdit, onDelete, onPreview, apiBaseUrl }) => {
  const formattedDate = new Date(blog.createdAt).toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' });
  const truncate = (html, len) => {
    const div = document.createElement('div');
    div.innerHTML = html;
    const text = div.textContent || div.innerText || '';
    return text.length > len ? text.substring(0, len) + '...' : text;
  };

  return (
    <div className="group bg-white dark:bg-dark-800 rounded-2xl shadow-card hover:shadow-card-hover overflow-hidden transition-all duration-300 hover:-translate-y-1">
      <div className="relative">
        {blog.image ? (
          <img src={`${apiBaseUrl}/${blog.image}`} alt={blog.title} className="w-full h-44 object-cover" loading="lazy" />
        ) : (
          <div className="w-full h-44 bg-gradient-to-br from-primary-100 to-primary-200 dark:from-dark-700 dark:to-dark-600 flex items-center justify-center">
            <FaBlog className="w-10 h-10 text-primary-300 dark:text-dark-500" />
          </div>
        )}
        {blog.status && blog.status !== 'Published' && (
          <div className="absolute top-3 left-3 z-10">
            {blog.status === 'Draft' ? (
              <span className="inline-flex items-center px-2 py-0.5 text-[0.65rem] font-bold bg-yellow-500/95 text-white backdrop-blur-sm rounded-lg shadow-sm">
                Draft
              </span>
            ) : (
              <span className="inline-flex items-center px-2 py-0.5 text-[0.65rem] font-bold bg-purple-600/95 text-white backdrop-blur-sm rounded-lg shadow-sm" title={blog.scheduledAt ? `Scheduled for: ${new Date(blog.scheduledAt).toLocaleString()}` : 'Scheduled'}>
                Scheduled
              </span>
            )}
          </div>
        )}
        <div className="absolute top-3 right-3 flex gap-1.5 opacity-0 group-hover:opacity-100 transition-all duration-200">
          <button onClick={() => onPreview(blog)} className="p-1.5 bg-white/90 dark:bg-dark-800/90 backdrop-blur-sm rounded-lg shadow-sm hover:bg-white dark:hover:bg-dark-700 text-gray-700 dark:text-gray-300 transition-all">
            <FaEye className="w-3 h-3" />
          </button>
          <button onClick={() => onEdit(blog)} className="p-1.5 bg-white/90 dark:bg-dark-800/90 backdrop-blur-sm rounded-lg shadow-sm hover:bg-white dark:hover:bg-dark-700 text-primary-600 transition-all">
            <FaEdit className="w-3 h-3" />
          </button>
          <button onClick={() => onDelete(blog._id)} className="p-1.5 bg-white/90 dark:bg-dark-800/90 backdrop-blur-sm rounded-lg shadow-sm hover:bg-white dark:hover:bg-dark-700 text-red-500 transition-all">
            <FaTrash className="w-3 h-3" />
          </button>
        </div>
      </div>
      <div className="p-5">
        <h3 className="text-sm font-bold text-gray-900 dark:text-white mb-1.5 line-clamp-2 leading-snug">{blog.title}</h3>
        <p className="text-xs text-gray-500 dark:text-gray-400 mb-3 line-clamp-2 leading-relaxed">{truncate(blog.description, 100)}</p>
        <div className="flex items-center justify-between">
          <span className="inline-flex items-center gap-1 px-2 py-0.5 text-[0.65rem] font-semibold bg-primary-50 text-primary-700 dark:bg-primary-900/30 dark:text-primary-400 rounded-full">
            <FaTag className="w-2 h-2" />{blog.category || "General"}
          </span>
          <span className="text-[0.65rem] text-gray-400 dark:text-gray-500">{formattedDate}</span>
        </div>
      </div>
    </div>
  );
});
BlogCard.displayName = 'BlogCard';

// ─── Main Component ─────────────────────────────────────────────────────
const AdminBlog = () => {
  const [blogs, setBlogs] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [success, setSuccess] = useState(null);
  const [searchQuery, setSearchQuery] = useState('');
  const [selectedCategory, setSelectedCategory] = useState('');
  const [categories, setCategories] = useState([]);
  const [viewMode, setViewMode] = useState('table'); // 'table' or 'grid'
  const [currentPage, setCurrentPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const [totalBlogs, setTotalBlogs] = useState(0);
  const blogsPerPage = 10;

  const [form, setForm] = useState({ title: "", slug: "", description: "", category: "", image: null, status: "Published", scheduledAt: "" });
  const [editForm, setEditForm] = useState({ id: "", title: "", slug: "", description: "", category: "", image: null, status: "Published", scheduledAt: "" });
  const [openEditDialog, setOpenEditDialog] = useState(false);
  const [openCreateDialog, setOpenCreateDialog] = useState(false);
  const [previewBlog, setPreviewBlog] = useState(null);
  const [selectedImage, setSelectedImage] = useState(null);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const quillRef = useRef(null);
  const editQuillRef = useRef(null);
  const searchTimeoutRef = useRef(null);

  const modules = {
    toolbar: [
      [{ 'header': [1, 2, 3, 4, 5, 6, false] }],
      [{ 'font': [] }], [{ 'size': [] }],
      ['bold', 'italic', 'underline', 'strike'],
      [{ 'color': [] }, { 'background': [] }],
      [{ 'script': 'sub' }, { 'script': 'super' }],
      [{ 'list': 'ordered' }, { 'list': 'bullet' }, { 'indent': '-1' }, { 'indent': '+1' }],
      [{ 'align': [] }], [{ 'direction': 'rtl' }],
      ['blockquote', 'code-block'],
      ['link', 'image', 'video'], ['clean']
    ],
  };
  const formats = [
    'header', 'font', 'size', 'bold', 'italic', 'underline', 'strike',
    'color', 'background', 'script', 'list', 'bullet', 'indent',
    'align', 'direction', 'blockquote', 'code-block', 'link', 'image', 'video'
  ];

  const apiBaseUrl = process.env.REACT_APP_API_BASE_URL;

  // Fetch categories
  useEffect(() => {
    const fetchCategories = async () => {
      try {
        const response = await fetch(`${apiBaseUrl}/api/blogs/categories`);
        if (response.ok) {
          const data = await response.json();
          setCategories(data.categories || []);
        }
      } catch (err) {
        console.error("Error fetching categories:", err);
      }
    };
    fetchCategories();
  }, [apiBaseUrl]);

  // Fetch blogs with pagination
  const fetchBlogs = useCallback(async (page = 1) => {
    setLoading(true);
    try {
      let url = `${apiBaseUrl}/api/blogs?page=${page}&limit=${blogsPerPage}&status=all`;
      if (selectedCategory) {
        url += `&category=${encodeURIComponent(selectedCategory)}`;
      }
      const response = await fetch(url);
      if (!response.ok) throw new Error(`HTTP error! status: ${response.status}`);
      const data = await response.json();
      setBlogs(data.blogs || data);
      setTotalPages(data.pages || 1);
      setTotalBlogs(data.total || 0);
      setCurrentPage(data.page || page);
    } catch (error) {
      setError(`Failed to fetch blogs: ${error.message}`);
    } finally {
      setLoading(false);
    }
  }, [apiBaseUrl, selectedCategory, blogsPerPage]);

  useEffect(() => {
    fetchBlogs(1);
  }, [fetchBlogs]);

  // Debounced search filter (client-side on current page)
  const filteredBlogs = blogs.filter(blog => {
    if (!searchQuery) return true;
    const q = searchQuery.toLowerCase();
    return (
      blog.title?.toLowerCase().includes(q) ||
      blog.category?.toLowerCase().includes(q) ||
      blog.slug?.toLowerCase().includes(q)
    );
  });

  const toLocalDateTimeString = (dateObj) => {
    if (!dateObj) return "";
    const d = new Date(dateObj);
    const tzOffset = d.getTimezoneOffset() * 60000;
    return (new Date(d.getTime() - tzOffset)).toISOString().slice(0, 16);
  };

  const saveDatabaseDraft = async (formToSave = form) => {
    const descVal = formToSave.description || "";
    const isFormEmpty = !formToSave.title.trim() && 
                        !formToSave.slug.trim() && 
                        (descVal.trim() === "" || descVal.trim() === "<p><br></p>") && 
                        !formToSave.category.trim() && 
                        !formToSave.image;
    
    const existingDraftId = localStorage.getItem('blog_draft_id');

    if (isFormEmpty) {
      if (existingDraftId) {
        try {
          await fetch(`${apiBaseUrl}/api/blogs/${existingDraftId}`, { method: "DELETE" });
        } catch (e) {
          console.error("Error deleting empty database draft:", e);
        }
        localStorage.removeItem('blog_draft_id');
      }
      localStorage.removeItem('blog_draft');
      return;
    }

    try {
      const formData = new FormData();
      formData.append('title', formToSave.title || 'Untitled Draft');
      formData.append('description', formToSave.description || '');
      formData.append('category', formToSave.category || 'General');
      formData.append('slug', formToSave.slug || `draft-${Date.now()}`);
      formData.append('status', 'Draft');
      if (formToSave.image) {
        formData.append('image', formToSave.image);
      }

      let response;
      if (existingDraftId) {
        response = await fetch(`${apiBaseUrl}/api/blogs/${existingDraftId}`, {
          method: "PUT",
          body: formData
        });
      } else {
        response = await fetch(`${apiBaseUrl}/api/blogs`, {
          method: "POST",
          body: formData
        });
      }

      if (response.ok) {
        const result = await response.json();
        const draftId = result.blog?._id || result.blog?.id;
        if (draftId) {
          localStorage.setItem('blog_draft_id', draftId);
        }
        localStorage.setItem('blog_draft', JSON.stringify({
          title: formToSave.title,
          slug: formToSave.slug,
          description: formToSave.description,
          category: formToSave.category,
          status: 'Draft',
          scheduledAt: ''
        }));
      }
    } catch (err) {
      console.error("Error autosaving draft to database:", err);
    }
  };

  const handleCloseCreate = async () => {
    setOpenCreateDialog(false);
    await saveDatabaseDraft();
    fetchBlogs(currentPage);
  };

  // ─── Handlers ───────────────────────────────────────────────────────
  const handleChange = (e) => {
    const { name, value } = e.target;
    setForm(prev => {
      const updated = { ...prev, [name]: value };
      if (name === "title" && !prev.slug) updated.slug = slugify(value);
      return updated;
    });
  };

  const handleSlugChange = (e) => {
    setForm(prev => ({ ...prev, slug: slugify(e.target.value) }));
  };

  const handleDescriptionChange = (value) => setForm(prev => ({ ...prev, description: value }));

  const handleImageChange = (e) => {
    const file = e.target.files[0];
    if (file) {
      if (!file.type.match('image.*')) { setError('Only image files are allowed'); return; }
      if (file.size > 20 * 1024 * 1024) { setError('Image size must be less than 20MB'); return; }
      setSelectedImage(URL.createObjectURL(file));
      setForm(prev => ({ ...prev, image: file }));
    }
  };

  const handleRemoveImage = () => { setForm(prev => ({ ...prev, image: null })); setSelectedImage(null); };

  const validateForm = () => {
    if (form.status !== 'Draft') {
      if (!form.title.trim()) { setError('Title is required'); return false; }
      if (!form.description.trim()) { setError('Description is required'); return false; }
      if (!form.slug.trim()) { setError('Slug is required'); return false; }
    } else {
      if (!form.title.trim()) { setError('Title is required to save draft'); return false; }
    }
    return true;
  };

  const handleSubmit = async () => {
    if (!validateForm()) return;
    setIsSubmitting(true); setError(null);
    try {
      const formData = new FormData();
      formData.append('title', form.title);
      formData.append('description', form.description);
      formData.append('category', form.category || 'General');
      formData.append('slug', form.slug);
      formData.append('status', form.status || 'Published');
      if (form.status === 'Scheduled') {
        formData.append('scheduledAt', form.scheduledAt);
      }
      if (form.image) formData.append('image', form.image);

      const existingDraftId = localStorage.getItem('blog_draft_id');
      let response;
      if (existingDraftId) {
        response = await fetch(`${apiBaseUrl}/api/blogs/${existingDraftId}`, { method: "PUT", body: formData });
      } else {
        response = await fetch(`${apiBaseUrl}/api/blogs`, { method: "POST", body: formData });
      }

      if (!response.ok) { const err = await response.json(); throw new Error(err.message || 'Failed to create blog'); }

      setSuccess(form.status === 'Scheduled' ? 'Blog scheduled successfully' : 'Blog created successfully');
      
      // Clear drafts
      localStorage.removeItem('blog_draft_id');
      localStorage.removeItem('blog_draft');

      fetchBlogs(currentPage);
      setForm({ title: "", slug: "", description: "", category: "", image: null, status: "Published", scheduledAt: "" });
      setSelectedImage(null);
      setOpenCreateDialog(false);
      // Refresh categories
      const catRes = await fetch(`${apiBaseUrl}/api/blogs/categories`);
      if (catRes.ok) { const catData = await catRes.json(); setCategories(catData.categories || []); }
    } catch (error) { setError(error.message); } finally { setIsSubmitting(false); }
  };

  const handleDelete = async (id) => {
    if (!window.confirm("Are you sure you want to move this blog post to the Recycle Bin?")) return;
    try {
      const response = await fetch(`${apiBaseUrl}/api/blogs/${id}`, { method: "DELETE" });
      if (!response.ok) throw new Error('Failed to delete blog');
      setSuccess('Blog moved to Recycle Bin');
      fetchBlogs(currentPage);
    } catch (error) { setError(error.message); }
  };

  const handleEditOpen = (blog) => {
    setEditForm({
      id: blog._id,
      title: blog.title,
      slug: blog.slug || "",
      description: blog.description,
      category: blog.category || "General",
      image: blog.image,
      status: blog.status || "Published",
      scheduledAt: blog.scheduledAt ? toLocalDateTimeString(blog.scheduledAt) : ""
    });
    setOpenEditDialog(true);
  };

  const handleEditChange = (e) => {
    const { name, value } = e.target;
    setEditForm(prev => {
      const updated = { ...prev, [name]: value };
      if (name === "title" && !prev.slug) updated.slug = slugify(value);
      return updated;
    });
  };

  const handleEditSlugChange = (e) => setEditForm(prev => ({ ...prev, slug: slugify(e.target.value) }));
  const handleEditDescriptionChange = (value) => setEditForm(prev => ({ ...prev, description: value }));
  const handleEditImageChange = (e) => {
    const file = e.target.files[0];
    if (file) {
      if (!file.type.match('image.*')) { setError('Only image files are allowed'); return; }
      if (file.size > 20 * 1024 * 1024) { setError('Image size must be less than 20MB'); return; }
      setEditForm(prev => ({ ...prev, image: file }));
    }
  };

  const validateEditForm = () => {
    if (editForm.status !== 'Draft') {
      if (!editForm.title.trim()) { setError('Title is required'); return false; }
      if (!editForm.description.trim()) { setError('Description is required'); return false; }
      if (!editForm.slug.trim()) { setError('Slug is required'); return false; }
    } else {
      if (!editForm.title.trim()) { setError('Title is required to save draft'); return false; }
    }
    return true;
  };

  const handleUpdate = async () => {
    if (!validateEditForm()) return;
    setIsSubmitting(true); setError(null);
    try {
      const formData = new FormData();
      formData.append('title', editForm.title);
      formData.append('description', editForm.description);
      formData.append('category', editForm.category);
      formData.append('slug', editForm.slug);
      formData.append('status', editForm.status || 'Published');
      if (editForm.status === 'Scheduled') {
        formData.append('scheduledAt', editForm.scheduledAt);
      }
      if (editForm.image instanceof File) formData.append('image', editForm.image);

      const response = await fetch(`${apiBaseUrl}/api/blogs/${editForm.id}`, { method: "PUT", body: formData });
      if (!response.ok) { const err = await response.json(); throw new Error(err.message || 'Failed to update blog'); }

      setSuccess('Blog updated successfully');
      fetchBlogs(currentPage);
      setOpenEditDialog(false);
    } catch (error) { setError(error.message); } finally { setIsSubmitting(false); }
  };

  // Auto-dismiss notifications
  useEffect(() => {
    if (error || success) {
      const timer = setTimeout(() => { setError(null); setSuccess(null); }, 4000);
      return () => clearTimeout(timer);
    }
  }, [error, success]);

  // Autosave draft on form changes
  useEffect(() => {
    if (openCreateDialog) {
      const descVal = form.description || "";
      const isFormEmpty = !form.title.trim() && 
                          !form.slug.trim() && 
                          (descVal.trim() === "" || descVal.trim() === "<p><br></p>") && 
                          !form.category.trim() && 
                          !form.image;
      if (isFormEmpty) {
        localStorage.removeItem('blog_draft');
      } else {
        const draftData = {
          title: form.title,
          slug: form.slug,
          description: form.description,
          category: form.category
        };
        localStorage.setItem('blog_draft', JSON.stringify(draftData));
      }
    }
  }, [form, openCreateDialog]);

  const handlePageChange = (page) => {
    if (page >= 1 && page <= totalPages) fetchBlogs(page);
  };

  // ─── Computed stats ─────────────────────────────────────────────────
  const latestBlog = blogs.length > 0 ? new Date(blogs[0]?.createdAt).toLocaleDateString('en-US', { month: 'short', day: 'numeric' }) : '-';

  return (
    <PageLayout>
      {/* Toast notifications */}
      {error && (
        <div className="fixed top-4 right-4 z-50 p-4 bg-red-50 dark:bg-red-900/20 border border-red-200 dark:border-red-800 rounded-xl text-red-700 dark:text-red-400 flex items-center gap-3 shadow-lg animate-fade-in">
          <span className="text-sm">{error}</span>
          <button onClick={() => setError(null)} className="text-red-500 hover:text-red-700 flex-shrink-0"><FaTimes /></button>
        </div>
      )}
      {success && (
        <div className="fixed top-4 right-4 z-50 p-4 bg-green-50 dark:bg-green-900/20 border border-green-200 dark:border-green-800 rounded-xl text-green-700 dark:text-green-400 flex items-center gap-3 shadow-lg animate-fade-in">
          <span className="text-sm">{success}</span>
          <button onClick={() => setSuccess(null)} className="text-green-500 hover:text-green-700 flex-shrink-0"><FaTimes /></button>
        </div>
      )}

      <PageHeader
        title="Blog Management"
        description={`${totalBlogs} blog posts`}
        icon={FaBlog}
        actions={
          <button
            onClick={() => {
              const savedDraft = localStorage.getItem('blog_draft');
              if (savedDraft) {
                try {
                  const draft = JSON.parse(savedDraft);
                  setForm({
                    title: draft.title || "",
                    slug: draft.slug || "",
                    description: draft.description || "",
                    category: draft.category || "",
                    status: draft.status || "Published",
                    scheduledAt: draft.scheduledAt || "",
                    image: null
                  });
                } catch (e) {
                  setForm({ title: "", slug: "", description: "", category: "", status: "Published", scheduledAt: "", image: null });
                }
              } else {
                setForm({ title: "", slug: "", description: "", category: "", status: "Published", scheduledAt: "", image: null });
              }
              setSelectedImage(null);
              setOpenCreateDialog(true);
            }}
            className="flex items-center gap-2 px-5 py-2.5 bg-gradient-to-r from-primary-600 to-primary-700 text-white font-medium rounded-xl shadow-lg shadow-primary-500/25 hover:shadow-xl hover:shadow-primary-500/30 transition-all duration-200 hover:-translate-y-0.5"
          >
            <FaPlus className="w-3.5 h-3.5" /> New Blog Post
          </button>
        }
      />

      {/* Stats Cards */}
      <div className="grid grid-cols-2 lg:grid-cols-4 gap-4">
        {[
          { label: 'Total Posts', value: totalBlogs, icon: FaBlog, gradient: 'from-blue-500 to-blue-600', shadow: 'shadow-blue-500/20' },
          { label: 'Categories', value: categories.length, icon: FaTag, gradient: 'from-emerald-500 to-emerald-600', shadow: 'shadow-emerald-500/20' },
          { label: 'Latest Post', value: latestBlog, icon: FaCalendar, gradient: 'from-amber-500 to-amber-600', shadow: 'shadow-amber-500/20' },
          { label: 'This Page', value: filteredBlogs.length, icon: FaList, gradient: 'from-rose-500 to-rose-600', shadow: 'shadow-rose-500/20' },
        ].map((stat, i) => (
          <div key={i} className="bg-white dark:bg-dark-800 rounded-2xl p-4 shadow-card border border-gray-100 dark:border-dark-700">
            <div className="flex items-center gap-3">
              <div className={`p-2.5 bg-gradient-to-br ${stat.gradient} rounded-xl shadow-lg ${stat.shadow}`}>
                <stat.icon className="w-4 h-4 text-white" />
              </div>
              <div>
                <p className="text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider">{stat.label}</p>
                <p className="text-xl font-bold text-gray-900 dark:text-white">{stat.value}</p>
              </div>
            </div>
          </div>
        ))}
      </div>

      {/* Toolbar: Search, Category Filter, View Toggle */}
      <div className="bg-white dark:bg-dark-800 rounded-2xl shadow-card border border-gray-100 dark:border-dark-700 p-4">
        <div className="flex flex-col md:flex-row gap-3">
          {/* Search */}
          <div className="relative flex-1">
            <FaSearch className="absolute left-3.5 top-1/2 -translate-y-1/2 text-gray-400 w-3.5 h-3.5" />
            <input
              type="text"
              placeholder="Search blogs by title, category, or slug..."
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              className="w-full pl-10 pr-4 py-2.5 bg-gray-50 dark:bg-dark-700 border border-gray-200 dark:border-dark-600 rounded-xl text-sm text-gray-900 dark:text-white placeholder-gray-400 focus:outline-none focus:ring-2 focus:ring-primary-500/50 focus:border-primary-500 transition-all"
            />
          </div>

          {/* Category Filter */}
          <select
            value={selectedCategory}
            onChange={(e) => { setSelectedCategory(e.target.value); setCurrentPage(1); }}
            className="px-4 py-2.5 bg-gray-50 dark:bg-dark-700 border border-gray-200 dark:border-dark-600 rounded-xl text-sm text-gray-900 dark:text-white focus:outline-none focus:ring-2 focus:ring-primary-500/50 focus:border-primary-500 transition-all min-w-[150px]"
          >
            <option value="">All Categories</option>
            {categories.map(cat => (
              <option key={cat} value={cat}>{cat}</option>
            ))}
          </select>

          {/* View Toggle */}
          <div className="flex bg-gray-100 dark:bg-dark-700 rounded-xl p-1">
            <button
              onClick={() => setViewMode('table')}
              className={`p-2.5 rounded-lg transition-all ${viewMode === 'table' ? 'bg-white dark:bg-dark-600 shadow-sm text-primary-600' : 'text-gray-400 hover:text-gray-600'}`}
              title="Table view"
            >
              <FaList className="w-3.5 h-3.5" />
            </button>
            <button
              onClick={() => setViewMode('grid')}
              className={`p-2.5 rounded-lg transition-all ${viewMode === 'grid' ? 'bg-white dark:bg-dark-600 shadow-sm text-primary-600' : 'text-gray-400 hover:text-gray-600'}`}
              title="Grid view"
            >
              <FaThLarge className="w-3.5 h-3.5" />
            </button>
          </div>
        </div>
      </div>

      {/* Content Area */}
      {loading ? (
        viewMode === 'table' ? (
          <div className="bg-white dark:bg-dark-800 rounded-2xl shadow-card border border-gray-100 dark:border-dark-700 overflow-hidden">
            <table className="w-full">
              <thead>
                <tr className="bg-gradient-to-r from-primary-600 to-primary-700 text-white">
                  <th className="px-5 py-4 text-left text-xs font-semibold uppercase tracking-wider w-16">#</th>
                  <th className="px-5 py-4 text-left text-xs font-semibold uppercase tracking-wider">Blog</th>
                  <th className="px-5 py-4 text-left text-xs font-semibold uppercase tracking-wider">Category</th>
                  <th className="px-5 py-4 text-left text-xs font-semibold uppercase tracking-wider">Status</th>
                  <th className="px-5 py-4 text-left text-xs font-semibold uppercase tracking-wider">Date</th>
                  <th className="px-5 py-4 text-center text-xs font-semibold uppercase tracking-wider">Actions</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-gray-100 dark:divide-dark-700">
                {[...Array(5)].map((_, i) => <TableRowSkeleton key={i} />)}
              </tbody>
            </table>
          </div>
        ) : (
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-5">
            {[...Array(8)].map((_, i) => <CardSkeleton key={i} />)}
          </div>
        )
      ) : viewMode === 'table' ? (
        /* ─── Table View ───────────────────────────────────────────── */
        <div className="bg-white dark:bg-dark-800 rounded-2xl shadow-card border border-gray-100 dark:border-dark-700 overflow-hidden">
          <div className="overflow-x-auto">
            <table className="w-full">
              <thead>
                <tr className="bg-gradient-to-r from-primary-600 to-primary-700 text-white">
                  <th className="px-5 py-4 text-left text-xs font-semibold uppercase tracking-wider w-16">#</th>
                  <th className="px-5 py-4 text-left text-xs font-semibold uppercase tracking-wider">Blog</th>
                  <th className="px-5 py-4 text-left text-xs font-semibold uppercase tracking-wider">Category</th>
                  <th className="px-5 py-4 text-left text-xs font-semibold uppercase tracking-wider">Status</th>
                  <th className="px-5 py-4 text-left text-xs font-semibold uppercase tracking-wider">Date</th>
                  <th className="px-5 py-4 text-center text-xs font-semibold uppercase tracking-wider">Actions</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-gray-100 dark:divide-dark-700">
                {filteredBlogs.length === 0 ? (
                  <tr>
                    <td colSpan="6" className="px-5 py-16 text-center">
                      <FaBlog className="w-10 h-10 text-gray-300 dark:text-dark-600 mx-auto mb-3" />
                      <p className="text-gray-500 dark:text-gray-400 font-medium">No blog posts found</p>
                      <p className="text-sm text-gray-400 dark:text-gray-500">Try adjusting your search or filters</p>
                    </td>
                  </tr>
                ) : (
                  filteredBlogs.map((blog, index) => (
                    <BlogTableRow
                      key={blog._id}
                      blog={blog}
                      index={(currentPage - 1) * blogsPerPage + index + 1}
                      onEdit={handleEditOpen}
                      onDelete={handleDelete}
                      onPreview={setPreviewBlog}
                      apiBaseUrl={apiBaseUrl}
                    />
                  ))
                )}
              </tbody>
            </table>
          </div>
        </div>
      ) : (
        /* ─── Grid View ────────────────────────────────────────────── */
        <div className="grid-view-container">
          {filteredBlogs.length === 0 ? (
            <div className="bg-white dark:bg-dark-800 rounded-2xl shadow-card border border-gray-100 dark:border-dark-700 p-16 text-center">
              <FaBlog className="w-10 h-10 text-gray-300 dark:text-dark-600 mx-auto mb-3" />
              <p className="text-gray-500 dark:text-gray-400 font-medium">No blog posts found</p>
              <p className="text-sm text-gray-400 dark:text-gray-500">Try adjusting your search or filters</p>
            </div>
          ) : (
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-5">
              {filteredBlogs.map((blog) => (
                <BlogCard
                  key={blog._id}
                  blog={blog}
                  onEdit={handleEditOpen}
                  onDelete={handleDelete}
                  onPreview={setPreviewBlog}
                  apiBaseUrl={apiBaseUrl}
                />
              ))}
            </div>
          )}
        </div>
      )}

      {/* Pagination */}
      {totalPages > 1 && !loading && (
        <div className="flex justify-center items-center gap-2">
          <button
            onClick={() => handlePageChange(currentPage - 1)}
            disabled={currentPage === 1}
            className="p-2.5 bg-white dark:bg-dark-800 border border-gray-200 dark:border-dark-700 rounded-xl text-gray-600 dark:text-gray-400 hover:bg-gray-50 dark:hover:bg-dark-700 disabled:opacity-40 disabled:cursor-not-allowed transition-all shadow-sm"
          >
            <FaChevronLeft className="w-3 h-3" />
          </button>

          {[...Array(totalPages)].map((_, i) => {
            const page = i + 1;
            // Show first, last, current, and adjacent pages
            if (page === 1 || page === totalPages || (page >= currentPage - 1 && page <= currentPage + 1)) {
              return (
                <button
                  key={page}
                  onClick={() => handlePageChange(page)}
                  className={`w-10 h-10 rounded-xl text-sm font-semibold transition-all shadow-sm ${currentPage === page
                      ? 'bg-gradient-to-r from-primary-600 to-primary-700 text-white shadow-lg shadow-primary-500/25'
                      : 'bg-white dark:bg-dark-800 border border-gray-200 dark:border-dark-700 text-gray-600 dark:text-gray-400 hover:bg-gray-50 dark:hover:bg-dark-700'
                    }`}
                >
                  {page}
                </button>
              );
            }
            if (page === currentPage - 2 || page === currentPage + 2) {
              return <span key={page} className="text-gray-400 px-1">...</span>;
            }
            return null;
          })}

          <button
            onClick={() => handlePageChange(currentPage + 1)}
            disabled={currentPage === totalPages}
            className="p-2.5 bg-white dark:bg-dark-800 border border-gray-200 dark:border-dark-700 rounded-xl text-gray-600 dark:text-gray-400 hover:bg-gray-50 dark:hover:bg-dark-700 disabled:opacity-40 disabled:cursor-not-allowed transition-all shadow-sm"
          >
            <FaChevronRight className="w-3 h-3" />
          </button>
        </div>
      )}

      {/* Preview Modal */}
      {previewBlog && createPortal(
        <div className="modal-overlay" onClick={() => setPreviewBlog(null)}>
          <div className="modal-content !max-w-5xl overflow-hidden" onClick={e => e.stopPropagation()}>
            <div className="flex items-center justify-between p-5 border-b border-gray-100 dark:border-dark-700">
              <h2 className="text-lg font-bold text-gray-900 dark:text-white">Preview</h2>
              <button onClick={() => setPreviewBlog(null)} className="p-2 text-gray-400 hover:text-gray-600 rounded-lg hover:bg-gray-100 dark:hover:bg-dark-700"><FaTimes /></button>
            </div>
            <div className="overflow-y-auto overflow-x-hidden max-h-[calc(90vh-80px)]">
              {previewBlog.image && (
                <img src={`${apiBaseUrl}/${previewBlog.image}`} alt={previewBlog.title} className="w-full h-64 object-cover" loading="lazy" />
              )}
              <div className="p-6">
                <span className="inline-flex items-center gap-1 px-2.5 py-1 text-xs font-semibold bg-primary-50 text-primary-700 dark:bg-primary-900/30 dark:text-primary-400 rounded-full mb-3">
                  <FaTag className="w-2.5 h-2.5" />{previewBlog.category || "General"}
                </span>
                <h1 className="text-2xl font-bold text-gray-900 dark:text-white mb-4">{previewBlog.title}</h1>
                <div className="prose max-w-none text-gray-700 dark:text-gray-300 text-sm leading-relaxed [&_p]:mb-2 [&_h2]:mb-3 [&_h2]:mt-5 [&_h2]:font-semibold [&_h3]:mb-2 [&_h3]:mt-4 [&_img]:max-w-full [&_img]:rounded [&_ul]:my-2 [&_ol]:my-2" dangerouslySetInnerHTML={{ __html: previewBlog.description }} />
              </div>
            </div>
          </div>
        </div>,
        document.body
      )}

      {/* Create Blog Modal */}
      {openCreateDialog && createPortal(
        <div className="modal-overlay" onClick={() => !isSubmitting && handleCloseCreate()}>
          <div className="modal-content !max-w-5xl overflow-hidden" onClick={e => e.stopPropagation()}>
            <div className="flex items-center justify-between p-5 border-b border-gray-100 dark:border-dark-700">
              <div>
                <h2 className="text-lg font-bold text-gray-900 dark:text-white">Create New Blog Post</h2>
                <p className="text-xs text-gray-500 dark:text-gray-400 mt-0.5">Images will be auto-optimized & converted to WebP</p>
              </div>
              <button onClick={() => !isSubmitting && handleCloseCreate()} disabled={isSubmitting} className="p-2 text-gray-400 hover:text-gray-600 rounded-lg hover:bg-gray-100 dark:hover:bg-dark-700"><FaTimes /></button>
            </div>
            <div className="p-6 overflow-y-auto overflow-x-hidden max-h-[calc(90vh-180px)] space-y-4">
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div>
                  <label className="block text-xs font-semibold text-gray-600 dark:text-gray-400 mb-1.5 uppercase tracking-wider">Title *</label>
                  <input type="text" name="title" value={form.title} onChange={handleChange} placeholder="Enter blog title..." className="w-full px-4 py-2.5 bg-gray-50 dark:bg-dark-700 border border-gray-200 dark:border-dark-600 rounded-xl text-sm text-gray-900 dark:text-white focus:outline-none focus:ring-2 focus:ring-primary-500/50 focus:border-primary-500 transition-all" />
                </div>
                <div>
                  <label className="block text-xs font-semibold text-gray-600 dark:text-gray-400 mb-1.5 uppercase tracking-wider">Category</label>
                  <input type="text" name="category" value={form.category} onChange={handleChange} placeholder="e.g. General, Tax, Business" className="w-full px-4 py-2.5 bg-gray-50 dark:bg-dark-700 border border-gray-200 dark:border-dark-600 rounded-xl text-sm text-gray-900 dark:text-white focus:outline-none focus:ring-2 focus:ring-primary-500/50 focus:border-primary-500 transition-all" />
                </div>
              </div>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div>
                  <label className="block text-xs font-semibold text-gray-600 dark:text-gray-400 mb-1.5 uppercase tracking-wider">Slug *</label>
                  <input type="text" name="slug" value={form.slug} onChange={handleSlugChange} className="w-full px-4 py-2.5 bg-gray-50 dark:bg-dark-700 border border-gray-200 dark:border-dark-600 rounded-xl text-sm text-gray-900 dark:text-white font-mono focus:outline-none focus:ring-2 focus:ring-primary-500/50 focus:border-primary-500 transition-all" />
                  <p className="text-[0.65rem] text-gray-400 mt-1">Auto-generated from title. You can customize it.</p>
                </div>
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                  <div>
                    <label className="block text-xs font-semibold text-gray-600 dark:text-gray-400 mb-1.5 uppercase tracking-wider">Status</label>
                    <select name="status" value={form.status || "Published"} onChange={handleChange} className="w-full px-4 py-2.5 bg-gray-50 dark:bg-dark-700 border border-gray-200 dark:border-dark-600 rounded-xl text-sm text-gray-900 dark:text-white focus:outline-none focus:ring-2 focus:ring-primary-500/50 focus:border-primary-500 transition-all">
                      <option value="Published">Publish Now</option>
                      <option value="Draft">Save as Draft</option>
                      <option value="Scheduled">Schedule Post</option>
                    </select>
                  </div>
                  {form.status === 'Scheduled' && (
                    <div>
                      <label className="block text-xs font-semibold text-gray-600 dark:text-gray-400 mb-1.5 uppercase tracking-wider">Publish Date & Time *</label>
                      <input type="datetime-local" name="scheduledAt" value={form.scheduledAt || ""} onChange={handleChange} className="w-full px-4 py-2.5 bg-gray-50 dark:bg-dark-700 border border-gray-200 dark:border-dark-600 rounded-xl text-sm text-gray-900 dark:text-white focus:outline-none focus:ring-2 focus:ring-primary-500/50 focus:border-primary-500 transition-all" required />
                    </div>
                  )}
                </div>
              </div>
              <div>
                <label className="block text-xs font-semibold text-gray-600 dark:text-gray-400 mb-1.5 uppercase tracking-wider">Description *</label>
                <ReactQuill ref={quillRef} theme="snow" value={form.description} onChange={handleDescriptionChange} modules={modules} formats={formats} className="bg-white dark:bg-dark-700 rounded-xl overflow-hidden blogs-editor" />
              </div>
              <div>
                <label className="block text-xs font-semibold text-gray-600 dark:text-gray-400 mb-1.5 uppercase tracking-wider">Featured Image</label>
                <input type="file" accept="image/*" onChange={handleImageChange} disabled={isSubmitting} className="block w-full text-sm text-gray-500 file:mr-4 file:py-2 file:px-4 file:rounded-lg file:border-0 file:text-xs file:font-semibold file:bg-primary-50 file:text-primary-700 hover:file:bg-primary-100 transition-all" />
                {selectedImage && (
                  <div className="mt-3 relative inline-block">
                    <img src={selectedImage} alt="Preview" className="max-h-28 rounded-xl shadow-sm" />
                    <button onClick={handleRemoveImage} disabled={isSubmitting} className="absolute -top-2 -right-2 p-1 bg-red-500 text-white rounded-full hover:bg-red-600 shadow-sm"><FaTimes className="w-2.5 h-2.5" /></button>
                  </div>
                )}
              </div>
            </div>
            <div className="flex justify-end gap-3 p-5 border-t border-gray-100 dark:border-dark-700 bg-gray-50/50 dark:bg-dark-800/50">
              <button onClick={handleCloseCreate} disabled={isSubmitting} className="px-5 py-2.5 text-sm text-gray-700 dark:text-gray-300 font-medium rounded-xl hover:bg-gray-100 dark:hover:bg-dark-700 transition-all">Cancel</button>
              <button onClick={handleSubmit} disabled={isSubmitting} className="px-5 py-2.5 text-sm bg-gradient-to-r from-primary-600 to-primary-700 text-white font-medium rounded-xl hover:shadow-lg hover:shadow-primary-500/25 disabled:opacity-50 transition-all">{isSubmitting ? 'Creating...' : 'Create Post'}</button>
            </div>
          </div>
        </div>,
        document.body
      )}

      {/* Edit Blog Modal */}
      {openEditDialog && createPortal(
        <div className="modal-overlay" onClick={() => !isSubmitting && setOpenEditDialog(false)}>
          <div className="modal-content !max-w-5xl overflow-hidden" onClick={e => e.stopPropagation()}>
            <div className="flex items-center justify-between p-5 border-b border-gray-100 dark:border-dark-700">
              <div>
                <h2 className="text-lg font-bold text-gray-900 dark:text-white">Edit Blog Post</h2>
                <p className="text-xs text-gray-500 dark:text-gray-400 mt-0.5">New images will be auto-optimized & converted to WebP</p>
              </div>
              <button onClick={() => !isSubmitting && setOpenEditDialog(false)} disabled={isSubmitting} className="p-2 text-gray-400 hover:text-gray-600 rounded-lg hover:bg-gray-100 dark:hover:bg-dark-700"><FaTimes /></button>
            </div>
            <div className="p-6 overflow-y-auto overflow-x-hidden max-h-[calc(90vh-180px)] space-y-4">
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div>
                  <label className="block text-xs font-semibold text-gray-600 dark:text-gray-400 mb-1.5 uppercase tracking-wider">Title *</label>
                  <input type="text" name="title" value={editForm.title} onChange={handleEditChange} className="w-full px-4 py-2.5 bg-gray-50 dark:bg-dark-700 border border-gray-200 dark:border-dark-600 rounded-xl text-sm text-gray-900 dark:text-white focus:outline-none focus:ring-2 focus:ring-primary-500/50 focus:border-primary-500 transition-all" />
                </div>
                <div>
                  <label className="block text-xs font-semibold text-gray-600 dark:text-gray-400 mb-1.5 uppercase tracking-wider">Category</label>
                  <input type="text" name="category" value={editForm.category} onChange={handleEditChange} className="w-full px-4 py-2.5 bg-gray-50 dark:bg-dark-700 border border-gray-200 dark:border-dark-600 rounded-xl text-sm text-gray-900 dark:text-white focus:outline-none focus:ring-2 focus:ring-primary-500/50 focus:border-primary-500 transition-all" />
                </div>
              </div>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div>
                  <label className="block text-xs font-semibold text-gray-600 dark:text-gray-400 mb-1.5 uppercase tracking-wider">Slug *</label>
                  <input type="text" name="slug" value={editForm.slug} onChange={handleEditSlugChange} className="w-full px-4 py-2.5 bg-gray-50 dark:bg-dark-700 border border-gray-200 dark:border-dark-600 rounded-xl text-sm text-gray-900 dark:text-white font-mono focus:outline-none focus:ring-2 focus:ring-primary-500/50 focus:border-primary-500 transition-all" />
                </div>
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                  <div>
                    <label className="block text-xs font-semibold text-gray-600 dark:text-gray-400 mb-1.5 uppercase tracking-wider">Status</label>
                    <select name="status" value={editForm.status || "Published"} onChange={handleEditChange} className="w-full px-4 py-2.5 bg-gray-50 dark:bg-dark-700 border border-gray-200 dark:border-dark-600 rounded-xl text-sm text-gray-900 dark:text-white focus:outline-none focus:ring-2 focus:ring-primary-500/50 focus:border-primary-500 transition-all">
                      <option value="Published">Publish Now</option>
                      <option value="Draft">Save as Draft</option>
                      <option value="Scheduled">Schedule Post</option>
                    </select>
                  </div>
                  {editForm.status === 'Scheduled' && (
                    <div>
                      <label className="block text-xs font-semibold text-gray-600 dark:text-gray-400 mb-1.5 uppercase tracking-wider">Publish Date & Time *</label>
                      <input type="datetime-local" name="scheduledAt" value={editForm.scheduledAt || ""} onChange={handleEditChange} className="w-full px-4 py-2.5 bg-gray-50 dark:bg-dark-700 border border-gray-200 dark:border-dark-600 rounded-xl text-sm text-gray-900 dark:text-white focus:outline-none focus:ring-2 focus:ring-primary-500/50 focus:border-primary-500 transition-all" required />
                    </div>
                  )}
                </div>
              </div>
              <div>
                <label className="block text-xs font-semibold text-gray-600 dark:text-gray-400 mb-1.5 uppercase tracking-wider">Description *</label>
                <ReactQuill ref={editQuillRef} theme="snow" value={editForm.description} onChange={handleEditDescriptionChange} modules={modules} formats={formats} className="bg-white dark:bg-dark-700 rounded-xl overflow-hidden blogs-editor" />
              </div>
              <div>
                <label className="block text-xs font-semibold text-gray-600 dark:text-gray-400 mb-1.5 uppercase tracking-wider">Featured Image</label>
                <input type="file" accept="image/*" onChange={handleEditImageChange} disabled={isSubmitting} className="block w-full text-sm text-gray-500 file:mr-4 file:py-2 file:px-4 file:rounded-lg file:border-0 file:text-xs file:font-semibold file:bg-primary-50 file:text-primary-700 hover:file:bg-primary-100 transition-all" />
                {editForm.image && !(editForm.image instanceof File) && (
                  <div className="mt-3">
                    <img src={`${apiBaseUrl}/${editForm.image}`} alt="Current" className="max-h-28 rounded-xl shadow-sm" loading="lazy" />
                  </div>
                )}
              </div>
            </div>
            <div className="flex justify-end gap-3 p-5 border-t border-gray-100 dark:border-dark-700 bg-gray-50/50 dark:bg-dark-800/50">
              <button onClick={() => setOpenEditDialog(false)} disabled={isSubmitting} className="px-5 py-2.5 text-sm text-gray-700 dark:text-gray-300 font-medium rounded-xl hover:bg-gray-100 dark:hover:bg-dark-700 transition-all">Cancel</button>
              <button onClick={handleUpdate} disabled={isSubmitting} className="px-5 py-2.5 text-sm bg-gradient-to-r from-primary-600 to-primary-700 text-white font-medium rounded-xl hover:shadow-lg hover:shadow-primary-500/25 disabled:opacity-50 transition-all">{isSubmitting ? 'Updating...' : 'Update Post'}</button>
            </div>
          </div>
        </div>,
        document.body
      )}
    </PageLayout>
  );
};

export default AdminBlog;